import type { Event, Invoice, QuoteDocument, User } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@kalos/ui';
import { CalendarIcon } from '@radix-ui/react-icons';
import { format, isValid, parseISO } from 'date-fns';
import { ChevronDown, ClipboardList, ExternalLink, FileText, HistoryIcon } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';

import { JOB_STATUS_COLORS, ROWS_PER_PAGE } from '../../../../../constants';
import { useEventBatchGetQuery } from '../../../../../hooks/react-query/useEventsQuery';
import { useInvoiceBatchGetQuery } from '../../../../../hooks/react-query/useInvoiceQuery';
import { useQuoteDocumentQuery } from '../../../../../hooks/react-query/useQuoteDocumentQuery';
import { JobHistoryDetail } from '../../../../ComponentsLibrary/ServiceCall/components/JobHistoryDetail';
import { PastProposals } from '../../../../JobDetails/components/PropertyTab/PastProposals';
import { useSchedulerStyle } from '../../context/schedulerStyle';
import { useLoadMoreAccumulator } from '../../hooks/useLoadMoreAccumulator';
import { LoadMoreButton, ShowLessButton, ShowMoreButton } from './LoadMoreButton';

// Number of rows shown before the "Show more" expander.
const COLLAPSED_COUNT = 3;

interface ReviewServiceHistoryProps {
  propertyId: number;
  customer: User;
}

const tabTriggerClass =
  'flex items-center gap-2 rounded-none py-3 text-gray-500 shadow-none data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-none first:rounded-none last:rounded-none';

export const ReviewServiceHistory = ({ propertyId, customer }: ReviewServiceHistoryProps) => {
  const userId = customer.id;
  const compact = useSchedulerStyle();
  const tabClass = `${tabTriggerClass} ${compact ? 'text-xs font-medium' : 'text-sm'}`;
  const [activeTab, setActiveTab] = useState('history');
  const [proposalsPage, setProposalsPage] = useState(0);
  const [proposalsExpanded, setProposalsExpanded] = useState(false);

  // --- Service History: server-side "Load More" pagination ---
  const [eventsPage, setEventsPage] = useState(0);
  const eventsQuery = useEventBatchGetQuery({
    filters: {
      propertyId,
      isActive: 1,
      orderBy: 'date_started',
      orderDir: 'DESC',
      pageNumber: eventsPage,
      onlyEvents: true,
    },
    enabled: !!propertyId,
  });
  const events = useLoadMoreAccumulator<Event, number>(
    propertyId,
    eventsQuery,
    eventsPage,
    setEventsPage,
  );

  // --- Invoices: server-side "Load More" pagination ---
  const [invoicesPage, setInvoicesPage] = useState(0);
  const invoicesQuery = useInvoiceBatchGetQuery({
    filter: { userId, pageNumber: invoicesPage },
    enabled: !!userId,
  });
  const invoices = useLoadMoreAccumulator<Invoice, number>(
    userId,
    invoicesQuery,
    invoicesPage,
    setInvoicesPage,
  );

  // --- Proposals ---
  // Compact (scheduler) accumulates pages via "Load More" like History/Invoices
  // so every proposal is reachable; the default tab keeps page-jump pagination.
  const proposalsQuery = useQuoteDocumentQuery({
    filters: {
      propertyId,
      orderBy: 'id',
      orderDir: 'DESC',
      pageNumber: proposalsPage,
    },
    enabled: !!propertyId,
  });
  const {
    data: proposalsData,
    isLoading: isProposalsLoading,
    error: proposalsError,
  } = proposalsQuery;
  const proposalsAcc = useLoadMoreAccumulator<QuoteDocument, number>(
    propertyId,
    proposalsQuery,
    proposalsPage,
    setProposalsPage,
  );

  const proposalsCount = proposalsData?.totalCount ?? 0;
  const proposalsPageCount = Math.ceil(proposalsCount / ROWS_PER_PAGE);
  const visibleProposals = proposalsExpanded
    ? proposalsAcc.items
    : proposalsAcc.items.slice(0, COLLAPSED_COUNT);

  return (
    <div className="flex flex-col overflow-hidden border-t border-gray-200">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
        <TabsList className="grid h-auto w-full shrink-0 grid-cols-3 rounded-none border-b bg-transparent p-0">
          <TabsTrigger value="history" className={tabClass}>
            <HistoryIcon className="h-4 w-4" />
            <span>Service History</span>
            <Badge variant="secondary" className="ml-1 rounded-full">
              {events.isLoading ? '…' : events.total}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="proposals" className={tabClass}>
            <FileText className="h-4 w-4" />
            <span>Proposals</span>
            <Badge variant="secondary" className="ml-1 rounded-full">
              {isProposalsLoading ? '…' : proposalsCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="invoices" className={tabClass}>
            <ClipboardList className="h-4 w-4" />
            <span>Invoices</span>
            <Badge variant="secondary" className="ml-1 rounded-full">
              {invoices.isLoading ? '…' : invoices.total}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="history"
          className={`mt-0 rounded-none p-4 shadow-none ${compact ? 'max-h-[40vh] overflow-y-auto [scrollbar-gutter:stable]' : ''}`}
        >
          <ServiceHistoryList
            events={events.items}
            total={events.total}
            isLoading={events.isLoading}
            hasMore={events.hasMore}
            isLoadingMore={events.isLoadingMore}
            onLoadMore={events.onLoadMore}
            compact={compact}
          />
        </TabsContent>

        <TabsContent
          value="proposals"
          className={`mt-0 rounded-none p-4 shadow-none ${compact ? 'max-h-[40vh] overflow-y-auto [scrollbar-gutter:stable]' : ''}`}
        >
          <PastProposals
            proposalsData={
              compact ? { results: visibleProposals, totalCount: proposalsCount } : proposalsData
            }
            variant={compact ? 'compact' : 'default'}
            isLoading={isProposalsLoading}
            error={proposalsError ?? null}
            jobCustomer={customer}
            currentPage={proposalsPage}
            onPageChange={setProposalsPage}
            pageCount={compact ? 1 : proposalsPageCount}
          />
          {compact && !proposalsExpanded && proposalsCount > COLLAPSED_COUNT && (
            <div className="pt-3">
              <ShowMoreButton
                remaining={proposalsCount - COLLAPSED_COUNT}
                onClick={() => setProposalsExpanded(true)}
              />
            </div>
          )}
          {compact && proposalsExpanded && (
            <div className="space-y-3 pt-3">
              {proposalsAcc.hasMore && (
                <LoadMoreButton
                  onClick={proposalsAcc.onLoadMore}
                  isLoading={proposalsAcc.isLoadingMore}
                />
              )}
              {proposalsCount > COLLAPSED_COUNT && (
                <ShowLessButton onClick={() => setProposalsExpanded(false)} />
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent
          value="invoices"
          className={`mt-0 rounded-none p-4 shadow-none ${compact ? 'max-h-[40vh] overflow-y-auto [scrollbar-gutter:stable]' : ''}`}
        >
          <InvoicesList
            invoices={invoices.items}
            total={invoices.total}
            isLoading={invoices.isLoading}
            hasMore={invoices.hasMore}
            isLoadingMore={invoices.isLoadingMore}
            onLoadMore={invoices.onLoadMore}
            compact={compact}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// --- Service History card list ---

const getStatusStyle = (logJobStatus: string) => {
  const status = logJobStatus || 'Requested';
  const hexColor = JOB_STATUS_COLORS[status] || JOB_STATUS_COLORS['Requested'];
  const isDarkStatus = ['Canceled', 'Archived'].includes(status);
  return {
    backgroundColor: `#${hexColor}`,
    color: isDarkStatus ? '#fff' : '#000',
  };
};

// Job-type pill color, keyed off the job type label.
const getJobTypeStyle = (jobType: string): string => {
  switch (jobType) {
    case 'Service':
      return 'bg-green-100 text-green-800 hover:bg-green-100';
    case 'Maintenance':
      return 'bg-purple-100 text-purple-800 hover:bg-purple-100';
    default:
      return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
  }
};

const ServiceHistoryList: FC<{
  events: Event[];
  total: number;
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  compact: boolean;
}> = ({ events, total, isLoading, hasMore, isLoadingMore, onLoadMore, compact }) => {
  const [openJobId, setOpenJobId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="bg-white">
            <CardContent className="flex items-center gap-3 p-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-5 w-28" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
        <HistoryIcon className="h-8 w-8" />
        <p className="mt-2 text-xs">No service history for this property</p>
      </div>
    );
  }

  const visibleEvents = compact && !expanded ? events.slice(0, COLLAPSED_COUNT) : events;

  const renderDate = (event: Event) =>
    event.dateStarted
      ? (() => {
          const d = parseISO(event.dateStarted);
          return isValid(d) ? format(d, 'M/d/yyyy') : 'No Date';
        })()
      : 'No Date';

  return (
    <div className="space-y-3">
      {visibleEvents.map((event) =>
        compact ? (
          <div
            key={event.id}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:shadow-md"
          >
            <div
              className="flex cursor-pointer items-center gap-3 p-3 hover:bg-gray-50"
              onClick={() => setOpenJobId(openJobId === event.id ? null : event.id)}
            >
              {event.jobType && (
                <Badge className={`shrink-0 text-xs font-medium ${getJobTypeStyle(event.jobType)}`}>
                  {event.jobType}
                </Badge>
              )}
              <span className="min-w-0 flex-1 truncate text-xs text-gray-700">
                {event.description}
              </span>
              <Badge
                style={getStatusStyle(event.logJobStatus || 'Pending')}
                className="shrink-0 text-xs font-medium leading-tight"
              >
                {event.logJobStatus || 'Pending'}
              </Badge>
              <span className="shrink-0 text-xs text-gray-400">{renderDate(event)}</span>
              <span className="shrink-0 text-xs font-medium text-gray-500">#{event.id}</span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                  openJobId === event.id ? 'rotate-180' : ''
                }`}
              />
            </div>
            {openJobId === event.id && <JobHistoryDetail event={event} size="compact" />}
          </div>
        ) : (
          <div key={event.id}>
            <Card
              className="cursor-pointer bg-white transition-all hover:bg-gray-50 hover:shadow-md"
              onClick={() => setOpenJobId(openJobId === event.id ? null : event.id)}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {event.jobType && (
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                      {event.jobType}
                    </Badge>
                  )}
                  {event.description && (
                    <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                      {event.description}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex-shrink-0 rounded-full bg-red-100 p-1.5">
                      <CalendarIcon className="h-4 w-4 text-red-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">{renderDate(event)}</p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-gray-800">
                          # Job {event.id}
                        </span>
                        <Badge
                          style={getStatusStyle(event.logJobStatus || 'Pending')}
                          className="text-xs leading-tight"
                        >
                          {event.logJobStatus || 'Pending'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                      openJobId === event.id ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </CardContent>
            </Card>
            {openJobId === event.id && <JobHistoryDetail event={event} />}
          </div>
        ),
      )}

      {compact ? (
        <>
          {!expanded && total > COLLAPSED_COUNT && (
            <ShowMoreButton remaining={total - COLLAPSED_COUNT} onClick={() => setExpanded(true)} />
          )}
          {expanded && (
            <>
              {hasMore && <LoadMoreButton onClick={onLoadMore} isLoading={isLoadingMore} />}
              <ShowLessButton onClick={() => setExpanded(false)} />
            </>
          )}
        </>
      ) : (
        hasMore && <LoadMoreButton onClick={onLoadMore} isLoading={isLoadingMore} />
      )}
    </div>
  );
};

// --- Invoice card list ---

const InvoicesList: FC<{
  invoices: Invoice[];
  total: number;
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  compact: boolean;
}> = ({ invoices, total, isLoading, hasMore, isLoadingMore, onLoadMore, compact }) => {
  const [openInvoiceId, setOpenInvoiceId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
        <ClipboardList className="h-8 w-8" />
        <p className="mt-2 text-xs">No invoices found for this customer</p>
      </div>
    );
  }

  const visibleInvoices = compact && !expanded ? invoices.slice(0, COLLAPSED_COUNT) : invoices;

  return (
    <div className="space-y-3">
      {visibleInvoices.map((invoice) => {
        const firstService = invoice.servicesperformedrow1;
        const amount = invoice.totalamounttotal
          ? `$${parseFloat(invoice.totalamounttotal).toFixed(2)}`
          : null;
        return compact ? (
          <div
            key={invoice.id}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:shadow-md"
          >
            <div
              className="flex cursor-pointer items-center gap-3 p-3 hover:bg-gray-50"
              onClick={() => setOpenInvoiceId(openInvoiceId === invoice.id ? null : invoice.id)}
            >
              <span className="shrink-0 text-xs font-semibold text-gray-900">
                Invoice #{invoice.id}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                {firstService ? `· ${firstService}` : ''}
              </span>
              {invoice.eventId > 0 && (
                <span className="shrink-0 text-xs text-gray-400">Job #{invoice.eventId}</span>
              )}
              {amount && (
                <span className="shrink-0 text-xs font-semibold text-gray-900">{amount}</span>
              )}
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                  openInvoiceId === invoice.id ? 'rotate-180' : ''
                }`}
              />
            </div>
            {openInvoiceId === invoice.id && <InvoiceDetail invoice={invoice} />}
          </div>
        ) : (
          <div key={invoice.id}>
            <Card
              className="cursor-pointer bg-white transition-all hover:bg-gray-50 hover:shadow-md"
              onClick={() => setOpenInvoiceId(openInvoiceId === invoice.id ? null : invoice.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 rounded-full bg-gray-100 p-2">
                      <ClipboardList className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-gray-900">
                          Invoice #{invoice.id}
                        </span>
                        {invoice.eventId > 0 && (
                          <span className="text-xs text-gray-500">Job #{invoice.eventId}</span>
                        )}
                      </div>
                      {firstService && (
                        <p className="mt-0.5 truncate text-xs text-gray-500">{firstService}</p>
                      )}
                      {invoice.systemType && (
                        <p className="mt-0.5 text-xs text-gray-500">{invoice.systemType}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {amount && (
                      <span className="text-sm font-semibold text-gray-900">{amount}</span>
                    )}
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        openInvoiceId === invoice.id ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            {openInvoiceId === invoice.id && <InvoiceDetail invoice={invoice} />}
          </div>
        );
      })}

      {compact ? (
        <>
          {!expanded && total > COLLAPSED_COUNT && (
            <ShowMoreButton remaining={total - COLLAPSED_COUNT} onClick={() => setExpanded(true)} />
          )}
          {expanded && (
            <>
              {hasMore && <LoadMoreButton onClick={onLoadMore} isLoading={isLoadingMore} />}
              <ShowLessButton onClick={() => setExpanded(false)} />
            </>
          )}
        </>
      ) : (
        hasMore && <LoadMoreButton onClick={onLoadMore} isLoading={isLoadingMore} />
      )}
    </div>
  );
};

const InvoiceDetail: FC<{ invoice: Invoice }> = ({ invoice }) => {
  const lineItems = useMemo(
    () =>
      [
        { service: invoice.servicesperformedrow1, amount: invoice.totalamountrow1 },
        { service: invoice.servicesperformedrow2, amount: invoice.totalamountrow2 },
        { service: invoice.servicesperformedrow3, amount: invoice.totalamountrow3 },
        { service: invoice.servicesperformedrow4, amount: invoice.totalamountrow4 },
      ].filter((row) => row.service || (row.amount && parseFloat(row.amount) !== 0)),
    [invoice],
  );

  return (
    <div className="border-t bg-gray-50 p-4">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {invoice.systemType && (
          <div>
            <span className="text-gray-500">System: </span>
            <span className="font-medium text-gray-900">{invoice.systemType}</span>
          </div>
        )}
        {invoice.totalamounttotal && (
          <div>
            <span className="text-gray-500">Total: </span>
            <span className="font-medium text-gray-900">
              ${parseFloat(invoice.totalamounttotal).toFixed(2)}
            </span>
          </div>
        )}
        {invoice.logPaymentType && (
          <div>
            <span className="text-gray-500">Payment: </span>
            <span className="font-medium text-gray-900">{invoice.logPaymentType}</span>
          </div>
        )}
        {invoice.logPaymentStatus && (
          <div>
            <span className="text-gray-500">Status: </span>
            <span className="font-medium text-gray-900">{invoice.logPaymentStatus}</span>
          </div>
        )}
      </div>

      {lineItems.length > 0 && (
        <div className="mt-3 space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">Services Performed</h4>
          {lineItems.map((row, i) => (
            <div
              key={i}
              className="flex items-start justify-between rounded border bg-white px-3 py-2 text-sm"
            >
              <span className="text-gray-700">{row.service || '—'}</span>
              {row.amount && parseFloat(row.amount) !== 0 && (
                <span className="ml-4 shrink-0 font-medium text-gray-900">
                  ${parseFloat(row.amount).toFixed(2)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {invoice.eventId > 0 && (
        <div className="mt-3 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/jobs/${invoice.eventId}`, '_blank');
            }}
          >
            View Full Job Details
            <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};
